<script setup>
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import FormInputText from '../fields/FormInputText.vue';
import FormInputSelect from '../fields/FormInputSelect.vue';

const emit = defineEmits(['advance-step']);

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    insurer: {
        type: Object,
        required: true
    }
});

const validationSchema = yup.object({
    title1: yup.string().required('Please enter a title'),
    titleOther: yup.string().when('title1', {
        is: 'Other',
        then: () => yup.string().required('Please specify a title'),
        otherwise: () => yup.string()
    }),
    email: yup.string().email().required('Please enter an email address'),
    firstName: yup.string().required('Please enter a first name'),
    lastName: yup.string().required('Please enter a last name'),
    dob: yup
        .string()
        .required('Please enter a date of birth')
        .matches(
            /^[0123][0-9]\/[01][0-9]\/[0-9]{4}$/,
            'Please enter a valid date of birth (DD/MM/YYYY)'
        ),
    phone: yup
        .string()
        .matches(
            /^(?:\+?61|0)[2-478](?:[ -]?[0-9]){8}$/,
            'Please enter a valid Australian phone number'
        )
        .required('Please enter a phone number')
});

const { errors, handleSubmit, defineField, setFieldValue } = useForm({
    validationSchema,
    initialValues: props.formData
});

const [title1] = defineField('title1');
const [titleOther] = defineField('titleOther');
const [firstName] = defineField('firstName');
const [middleName] = defineField('middleName');
const [lastName] = defineField('lastName');
const [dob] = defineField('dob');
const [email] = defineField('email');
const [phone] = defineField('phone');

// It validates all fields and doesn't call your function unless all fields are valid
const onSubmit = handleSubmit((values) => {
    // Set title to titleOther if title is 'Other'
    if (values.title1 === 'Other') values.title1 = values.titleOther;

    const urlParams = new URLSearchParams(window.location.search);
    emit('advance-step', { ...values, userUniqueId: urlParams.get('id') });
});
</script>
<template>
    <form @submit="onSubmit">
        <h2 class="h2 panel-heading">We’ll need a few more details about you to get you started</h2>
        <div class="form-panel">
            <div class="wysiwyg-output tw-mb-6">
                <h3 class="tw-mb-4">Life Insured Details</h3>
                <p>
                    To make sure your form can be successfully retrieved, we require your contact
                    details.
                </p>
            </div>
            <FormInputSelect
                :value="title1"
                :error="errors.title1"
                name="title1"
                placeholder="Title"
                :options="['Mr', 'Ms', 'Mrs', 'Miss', 'Other']"
                @update="setFieldValue('title1', $event)"
            />
            <FormInputText
                v-if="title1 === 'Other'"
                :value="titleOther"
                :error="errors.titleOther"
                name="titleOther"
                placeholder="If other, please specify"
                @update="setFieldValue('titleOther', $event)"
            />
            <FormInputText
                :value="firstName"
                :error="errors.firstName"
                name="firstName"
                placeholder="First Name"
                @update="setFieldValue('firstName', $event)"
            />
            <FormInputText
                :value="middleName"
                :error="errors.middleName"
                name="middleName"
                placeholder="Middle Name"
                @update="setFieldValue('middleName', $event)"
            />
            <FormInputText
                :value="lastName"
                :error="errors.lastName"
                name="lastName"
                placeholder="Last Name"
                @update="setFieldValue('lastName', $event)"
            />

            <FormInputText
                :value="dob"
                :error="errors.dob"
                mask="##/##/####"
                name="dob"
                placeholder="Date of birth (DD/MM/YYYY)"
                @update="setFieldValue('dob', $event)"
            />

            <FormInputText
                :value="email"
                :error="errors.email"
                name="email"
                placeholder="Email"
                @update="setFieldValue('email', $event)"
            />
            <FormInputText
                :value="phone"
                :error="errors.phone"
                name="phone"
                placeholder="Phone"
                @update="setFieldValue('phone', $event)"
            />
        </div>

        <button class="btn btn--green btn--full">Next</button>
    </form>
</template>
